import { Button, Col, Row } from 'react-bootstrap';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setFilters } from '../../../../redux/slices/filters';
import moment from 'moment';
import MePosDateFilter from './mePosDateFilter';
import TypesFilter from './TypesFilter';
import MasterSelectFilter from './MasterSelectFilter';
import HaircutSelectFilter from './HaircutSelectFilter';
import ServiceSelectFilter from './ServiceSelectFilter';
import { useTranslation } from 'react-i18next';
import useRequestLocation from '../../../../hooks/useRequestLocation';
import { getFormattedPeriod, isSameMaster } from './utils';

// const EXCLUDED_FIELDS = [
//   'location',
//   'keywords',
//   'gclid',
//   'audience',
//   'unique',
//   'premium',
//   'banner-id',
//   'is-react-qr-generator',
//   'entry-connected-titles',
//   'me-city-connected',
//   'user-email',
//   'user-earned',
//   'unique-one-day',
//   'banner-type',
//   'entry_has_10_scans',
//   'ads_earned',
//   'exact-location'
// ];

const START_DATE_RANGE = {
  selection1: {
    startDate: moment().subtract(1, 'week').toDate(),
    endDate: moment().toDate(),
    key: 'selection1'
  }
};

const TYPE_OF_STATISTIC = 'DAILY_STATISTIC';
const RESET_FILTERS_BASE = {
  qrIds: [],
  location: '',
  device: '',
  isCompareMode: false,
  secondPeriodStart: null,
  secondPeriodEnd: null,
  mode: 'period',
  isFitersActive: false,
  service: 'all',
  haircutType: 'all',
  eventsArray: []
};

const Filters = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { getFilialWithPermissions } = useRequestLocation();
  const [dateRange, setDateRange] = useState(START_DATE_RANGE);
  const [statisticType, setStatisticType] = useState(TYPE_OF_STATISTIC);
  const [master, setMaster] = useState('all');
  const [haircutType, setHaircutType] = useState('all');
  const [service, setService] = useState('all');
  const [isEmptyMastersData, setIsEmptyMastersData] = useState(null);
  const [selfPermission, setSelfPermission] = useState(false);
  const [defaultSelfMaster, setDefaultSelfMaster] = useState(null);

  const appliedFilters = useSelector((state) => state.filters);
  const { periodStart, periodEnd } = appliedFilters;
  const getFilialWithPermissionsRef = useRef(getFilialWithPermissions);

  getFilialWithPermissionsRef.current = getFilialWithPermissions;

  useEffect(() => {
    const { periodToStart, periodToEnd } = getFormattedPeriod(periodStart, periodEnd);

    getFilialWithPermissionsRef.current(periodToStart, periodToEnd).then((data) => {
      const hasSelfPermission = !!(data?.selfPermission);
      setSelfPermission(hasSelfPermission);
      if (!hasSelfPermission) {
        setDefaultSelfMaster(null);
      }
    });
  }, [periodStart, periodEnd]);

  const handleSetMastersData = (isEmpty) => {
    setIsEmptyMastersData(isEmpty);
  };

  const syncAppliedMaster = (nextMaster) => {
    if (isSameMaster(appliedFilters.master, nextMaster)) {
      return;
    }
    dispatch(setFilters({
      ...appliedFilters,
      master: nextMaster
    }));
  };

  const handleApply = () => {
    if (
      !statisticType &&
      master === 'all' &&
      haircutType === 'all' &&
      service === 'all' &&
      JSON.stringify(dateRange) === JSON.stringify(START_DATE_RANGE)
    ) {
      return;
    }
    const filterOptions = {
      ...appliedFilters,
      statisticType: statisticType,
      master: master,
      haircutType: haircutType,
      service: service,
      periodStart: dateRange.selection1.startDate
        ? moment(dateRange.selection1.startDate).startOf('D').toDate()
        : null,
      periodEnd: dateRange.selection1.endDate
        ? moment(dateRange.selection1.endDate).endOf('D').toDate()
        : null
    };
    dispatch(setFilters(filterOptions));
  };

  const handleReset = () => {
    const resetMaster = selfPermission && defaultSelfMaster ? defaultSelfMaster : 'all';
    const resetPeriodStart = moment().subtract(1, 'week');
    const resetPeriodEnd = moment();

    setMaster(resetMaster);
    setHaircutType('all');
    setService('all');
    setDateRange(START_DATE_RANGE);
    setStatisticType(TYPE_OF_STATISTIC);
    dispatch(setFilters({
      ...RESET_FILTERS_BASE,
      statisticType: TYPE_OF_STATISTIC,
      master: resetMaster,
      periodStart: resetPeriodStart,
      periodEnd: resetPeriodEnd
    }));
  };

  return (
    <div className="d-flex flex-column justify-content-between mb-5">
      <Row className="gx-3 gy-3">
        <Col lg={12} xxl={4}>
          <Row className="g-3">
            <Col sm={6} lg={5} xxl={6}>
              <MePosDateFilter
                dateRange={dateRange}
                setDateRange={setDateRange}
                startDateRange={START_DATE_RANGE}
              />
            </Col>
            <Col sm={6} lg={7} xxl={6}>
              <TypesFilter
                selectedType={statisticType}
                setSelectedType={setStatisticType}
              />
            </Col>
          </Row>
        </Col>

        <Col lg={8} xxl={5}>
          <Row className="g-3">
            <Col sm={6} lg={4}>
              <MasterSelectFilter
                startData={periodStart}
                endData={periodEnd}
                selectedMaster={master}
                setSelectedMaster={setMaster}
                haircutType={haircutType}
                service={service}
                handleSetMastersData={handleSetMastersData}
                selfPermission={selfPermission}
                defaultSelfMaster={defaultSelfMaster}
                setDefaultSelfMaster={setDefaultSelfMaster}
                syncAppliedMaster={syncAppliedMaster}
              />
            </Col>
            <Col sm={6} lg={4}>
              <HaircutSelectFilter
                startData={periodStart}
                endData={periodEnd}
                selectedMaster={master}
                setHairstyles={setHaircutType}
                selectedHairstyle={haircutType}
                setService={setService}
              />
            </Col>
            <Col sm={12} lg={4}>
              <ServiceSelectFilter
                startData={periodStart}
                endData={periodEnd}
                master={master}
                haircutType={haircutType}
                service={service}
                setService={setService}
              />
            </Col>
          </Row>
        </Col>

        <Col lg={4} xxl={3}>
          <Row className="h-100 gx-3 ps-lg-2">
            <Col xs={6}>
              <Button
                variant="outline-dark"
                className="btn w-100 rounder-4px px-1 clear-btn"
                onClick={handleReset}
              >
                {t('filters.cancel')}
              </Button>
            </Col>
            <Col xs={6}>
              <Button
                variant="pos-main"
                className="btn w-100 rounder-4px px-1 apply-btn text-white"
                onClick={handleApply}
              >
                {t('filters.apply')}
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>
      {isEmptyMastersData && (
        <h3 className="mt-5 text-lg-center text-start">
          {t('statistic.noData')}
        </h3>
      )}
    </div>
  );
};

export default Filters;
